/**
 * Cirque Los Angeles: small behaviors that go with assets/cirque.css.
 */

/**
 * Lets the menu's closing animation play when it is closed from the header icon.
 *
 * The icon is the <summary> of a <details>, and the browser closes a <details> the instant its
 * summary is clicked, so the sheet would vanish before the theme's animated close could show.
 * Cancelling that default leaves the theme's own handler to close the menu, which waits for the
 * animation before it removes `open`.
 */
document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('header-drawer summary.header__icon--menu');
    const details = summary?.parentElement;

    // `open` is still set here whichever listener runs first; the theme's handler may already
    // have removed the `menu-open` class.
    if (details instanceof HTMLDetailsElement && details.open) {
      event.preventDefault();
    }
  },
  { capture: true }
);

/** How long one full write of the logo takes, with a little air after it. */
const LOGO_WRITE_MS = 2600;

/**
 * Plays the pen-drawn logo animation on one logo, unless it is already mid-write.
 * @param {Element | null | undefined} logo - An element carrying the `cq-logo` class.
 */
function writeLogo(logo) {
  if (!(logo instanceof HTMLElement) || !logo.classList.contains('cq-logo')) return;

  const now = performance.now();
  if (now - Number(logo.dataset.cqWroteAt ?? -LOGO_WRITE_MS) < LOGO_WRITE_MS) return;

  logo.dataset.cqWroteAt = String(now);
  logo.classList.remove('is-writing');
  void logo.offsetWidth; // restarts the CSS animations
  logo.classList.add('is-writing');
}

/**
 * Swaps the footer logo image for a vector copy that can be drawn stroke by stroke (see
 * "Pen-drawn logo" in assets/cirque.css). It is written out when the footer comes into view.
 * If the vector can't be loaded the normal logo image stays as it is.
 * The header logo is left alone: no animation on it.
 */
async function setUpWrittenLogos() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const holders = document.querySelectorAll('.page-wrapper > footer .logo-block__image-wrapper');
  if (holders.length === 0) return;

  let markup = '';
  try {
    const response = await fetch(new URL('cirque-logo.svg', import.meta.url));
    if (!response.ok) return;
    markup = await response.text();
  } catch {
    return;
  }
  if (!markup.includes('cq-logo__svg')) return;

  for (const holder of holders) {
    if (!holder.querySelector('img') || holder.querySelector('.cq-logo__svg')) continue;
    holder.insertAdjacentHTML('beforeend', markup);
    holder.classList.add('cq-logo');
  }

  // The footer may already be in view by the time the vector arrives
  const footer = document.querySelector('.page-wrapper > footer.cq-footer--in');
  if (footer) writeLogo(footer.querySelector('.cq-logo'));
}


/**
 * Footer entrance. Marks the footer once the end of the page content nears the bottom of the
 * screen, which is when the footer starts to come into view, so the logo can be written out and
 * the lines under it can rise (see the Footer section of assets/cirque.css).
 */
function watchFooter() {
  const footer = document.querySelector('.page-wrapper > footer');
  const main = document.getElementById('MainContent');
  if (!footer || !main || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'height: 1px; margin-top: -1px; pointer-events: none;';
  main.append(sentinel);

  footer.classList.add('cq-footer--ready');

  new IntersectionObserver(
    ([entry]) => {
      if (!entry) return;

      const inView = entry.isIntersecting || entry.boundingClientRect.top < 0;
      const wasInView = footer.classList.contains('cq-footer--in');
      footer.classList.toggle('cq-footer--in', inView);
      if (inView && !wasInView) writeLogo(footer.querySelector('.cq-logo'));
    },
    { rootMargin: '0px 0px -15% 0px' }
  ).observe(sentinel);
}


/**
 * Product cards rise in as they scroll into view, one after the next.
 * Cards added later (filtering, section re-renders, quick loads) are picked up too, and so are
 * cards the theme makes by re-classing an element that was already on the page: "You may also
 * like" turns its loading placeholders into the first cards that way.
 */
function revealProductCards() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /** Cards already handed to the observer, so a re-render never hides one a second time. */
  const held = new WeakSet();

  const observer = new IntersectionObserver(
    (entries) => {
      let order = 0;

      for (const entry of entries) {
        if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;

        entry.target.style.setProperty('--cq-stagger', String(order));
        entry.target.classList.add('cq-in');
        entry.target.classList.remove('cq-wait');
        observer.unobserve(entry.target);
        order += 1;
      }
    },
    { rootMargin: '0px 0px -6% 0px' }
  );

  const hold = (/** @type {Element} */ item) => {
    if (held.has(item)) return;

    held.add(item);

    // A card that is already on screen stays as it is. Hiding it now, only to fade it back in,
    // would make it blink whenever this script starts after the page has been painted.
    const box = item.getBoundingClientRect();
    if (box.width > 0 && box.top < window.innerHeight && box.bottom > 0) return;

    item.classList.add('cq-wait');
    observer.observe(item);
  };

  const watch = (/** @type {ParentNode} */ root) => {
    for (const item of root.querySelectorAll('.resource-list__item')) hold(item);
  };

  watch(document);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        const target = mutation.target;
        if (target instanceof Element && target.classList.contains('resource-list__item')) hold(target);
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('.resource-list__item')) hold(node);
        else watch(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
}


/** Degrees the crossbones menu icon turns per pixel scrolled: one full turn every 400px. */
const MENU_ICON_DEGREES_PER_PIXEL = 0.9;

/**
 * Turns the crossbones menu icon as the page scrolls (see "Menu button" in assets/cirque.css).
 * The rotation is written straight onto the icon, once per frame. Setting a CSS variable on <html>
 * instead would make the browser restyle the whole page on every scroll frame, which is what made
 * an earlier version lag. Only the page's own scroller counts: the document on small screens, the
 * theme's .page-wrapper on large ones. Drawers and carousels are ignored.
 */
function turnMenuIconOnScroll() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /** @type {SVGElement | null} */
  let icon = null;
  /** @type {number | null} */
  let frame = null;
  let scrollTop = 0;

  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      const isPage = target === document || (target instanceof Element && target.classList.contains('page-wrapper'));
      if (!isPage) return;

      scrollTop = target instanceof Element ? target.scrollTop : document.scrollingElement?.scrollTop ?? 0;
      if (frame !== null) return;

      frame = requestAnimationFrame(() => {
        frame = null;
        if (!icon?.isConnected) {
          icon = document.querySelector('#header-component .header__icon--menu .header-drawer-icon--open svg');
        }
        if (icon) icon.style.rotate = `${(scrollTop * MENU_ICON_DEGREES_PER_PIXEL).toFixed(1)}deg`;
      });
    },
    { passive: true, capture: true }
  );
}


/**
 * Shows a card's second photo only when the mouse is really moved onto it.
 *
 * The theme flips a card to its next photo on `pointerenter` and back on `pointerleave`. Browsers
 * also fire those when the page scrolls a card under a cursor that is sitting still, so scrolling
 * down with the cursor parked left whichever card landed under it on its back photo. Here the
 * theme's enter handler is held back, and the flip starts on the first real mouse movement over
 * the photo instead. Touch screens are untouched: the theme never flips photos for a finger.
 * Cards inside a carousel, and cards at phone widths, never flip: there they always show the
 * first photo.
 */
function flipCardPhotoOnDeliberateHover() {
  const GALLERY = '.card-gallery[on\\:pointerenter]';

  const WIDE_SCREEN = window.matchMedia('(min-width: 750px)');

  /** Galleries that are on their second photo because the mouse moved onto them. */
  const flipped = new WeakSet();

  /** Where the mouse last was on the screen, to tell a real move from the page moving under it. */
  /** @type {{ x: number, y: number } | null} */
  let lastPosition = null;

  /** @param {EventTarget | null} target */
  const isGallery = (target) => target instanceof Element && target.matches(GALLERY);

  // These listen on window, which hears an event before the theme's listeners on document do.
  window.addEventListener(
    'pointerenter',
    (event) => {
      if (event.pointerType === 'mouse' && isGallery(event.target)) event.stopPropagation();
    },
    { capture: true }
  );

  window.addEventListener(
    'pointerleave',
    (event) => {
      if (event.pointerType !== 'mouse' || !isGallery(event.target)) return;

      // Only a card that was flipped is flipped back. The theme's reset steps one photo back
      // whatever is showing, which on an unflipped card would wrap round to its last photo.
      if (!flipped.delete(/** @type {Element} */ (event.target))) event.stopPropagation();
    },
    { capture: true }
  );

  window.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse') return;

      const moved = lastPosition !== null && (lastPosition.x !== event.screenX || lastPosition.y !== event.screenY);
      lastPosition = { x: event.screenX, y: event.screenY };
      if (!moved || !(event.target instanceof Element)) return;

      const gallery = event.target.closest(GALLERY);
      if (!gallery || flipped.has(gallery)) return;

      // Not in a carousel (the card is one of its slides), and not at phone widths, where a
      // mouse only shows up when the layout is being previewed in a narrow desktop window.
      if (gallery.closest('slideshow-slide') || !WIDE_SCREEN.matches) return;

      const card = /** @type {any} */ (gallery.closest('product-card'));
      const slideshow = /** @type {any} */ (gallery.querySelector('slideshow-component'));
      if (typeof card?.previewImage !== 'function' || !slideshow) return;

      // Count the card as flipped only if its photo really changed, so the reset stays in step
      const before = slideshow.current;
      card.previewImage(event);
      if (slideshow.current !== before) flipped.add(gallery);
    },
    { capture: true, passive: true }
  );
}


/**
 * Links to a product page drawer open that drawer in place instead of jumping: "Size guide" beside
 * the size buttons (snippets/variant-main-picker.liquid) opens Sizing, and the reviews line under the
 * price (blocks/cirque-reviews-summary.liquid) opens Reviews. Arriving with #sizing or #reviews in
 * the address does the same, and so does coming back from sending a review.
 */
function openDrawersFromLinks() {
  /**
   * @param {string} id
   * @param {Element} [target] - What to bring into view instead of the top of the drawer
   */
  const openDrawer = (id, target) => {
    const drawer = document.getElementById(id);
    if (!(drawer instanceof HTMLDetailsElement)) return false;

    if (!drawer.open) drawer.querySelector('summary')?.click();
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else drawer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  };

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a.variant-option__size-guide, a.cirque-reviews-summary__link') : null;
    if (!(link instanceof HTMLAnchorElement) || link.closest('dialog')) return;

    // Only when the link is for the product already on screen
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    if (openDrawer(url.hash.slice(1))) event.preventDefault();
  });

  // After a review is sent the page reloads at the top, so show the thank-you or the problem
  const reviewAnswer = document.querySelector('#reviews .cirque-review-form__sent, #reviews .cirque-review-form__error');
  const fromAddress = location.hash.slice(1);
  if (reviewAnswer) {
    window.setTimeout(() => openDrawer('reviews', reviewAnswer), 300);
  } else if (fromAddress === 'sizing' || fromAddress === 'reviews' || fromAddress === 'shipping') {
    window.setTimeout(() => openDrawer(fromAddress), 300);
  } else if (new URLSearchParams(location.search).has('contact_posted') && document.getElementById('reviews')) {
    window.setTimeout(() => openDrawer('reviews'), 300);
  }
}

/**
 * The review form (blocks/cirque-product-tabs.liquid) says in words what is missing when Submit is
 * pressed too early. The phone's own warning has nowhere to point for the star rating, whose inputs
 * sit hidden under the stars, so a skipped rating looked like a Submit button that did nothing.
 */
function explainReviewFormProblems() {
  /** @type {WeakSet<HTMLFormElement>} */
  const explaining = new WeakSet();

  document.addEventListener(
    'invalid',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

      const form = field.form;
      if (!form?.classList.contains('cirque-review-form')) return;
      event.preventDefault();

      // Every field that is not ready reports in turn; the first one is explained
      if (explaining.has(form)) return;
      explaining.add(form);
      window.setTimeout(() => explaining.delete(form), 0);

      const owner = field.closest('[data-missing]');
      const message =
        field.validity.typeMismatch && field instanceof HTMLInputElement
          ? field.dataset.mismatch
          : owner instanceof HTMLElement
            ? owner.dataset.missing
            : field.validationMessage;

      for (const marked of form.querySelectorAll('[aria-invalid="true"]')) marked.removeAttribute('aria-invalid');
      field.setAttribute('aria-invalid', 'true');

      // The message moves under the field it is about, so both are in view together
      const problem = form.querySelector('.cirque-review-form__problem');
      if (problem) {
        (owner ?? field).after(problem);
        problem.textContent = message || field.validationMessage;
      }

      (owner ?? field).scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus({ preventScroll: true });
    },
    true
  );

  /** @param {Event} event */
  const clearWhenFixed = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

    const form = field.form;
    if (!form?.classList.contains('cirque-review-form') || !field.validity.valid) return;

    const marked = form.querySelector('[aria-invalid="true"]');
    const sameField = marked === field || (marked instanceof HTMLInputElement && marked.name === field.name);
    if (!sameField) return;

    marked?.removeAttribute('aria-invalid');
    const problem = form.querySelector('.cirque-review-form__problem');
    if (problem) problem.textContent = '';
  };

  document.addEventListener('input', clearWhenFixed);
  document.addEventListener('change', clearWhenFixed);
}

/**
 * Starts each feature on its own, so a problem in one can never stop the others.
 * @param {Array<() => unknown>} features
 */
function start(features) {
  for (const feature of features) {
    try {
      Promise.resolve(feature()).catch((error) => console.error('[cirque]', error));
    } catch (error) {
      console.error('[cirque]', error);
    }
  }
}

/**
 * Shows that a review is on its way, and says so plainly when it never leaves the page.
 *
 * Shopify's spam check (hCaptcha, added to the form by Shopify) holds each review until it has
 * checked it. It drops the review without a word when it is not ready: if Submit is pressed while it
 * is still loading, or when something on the phone stops it from loading at all. When it is still
 * loading, the review is sent again as soon as it is ready.
 */
function showReviewSending() {
  /** Whether the spam check's picture puzzle is open, which can take the shopper a while. */
  const puzzleOpen = () =>
    [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some((frame) => {
      const box = frame.getBoundingClientRect();
      return /challenge/.test(frame.src) && box.width > 0 && getComputedStyle(frame).visibility === 'visible';
    });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cirque-review-form')) return;

    const button = form.querySelector('.cirque-review-form__submit');
    const problem = form.querySelector('.cirque-review-form__problem');
    if (!(button instanceof HTMLButtonElement)) return;

    const label = button.dataset.label ?? button.textContent?.trim() ?? '';
    button.dataset.label = label;
    button.textContent = button.dataset.sending ?? label;
    if (problem) problem.textContent = '';

    // The spam check dropped this one because it is still loading: send again once it is ready
    if (!form.hasAttribute('data-hcaptcha-bound')) {
      const ready = new MutationObserver(() => {
        if (!form.hasAttribute('data-hcaptcha-bound')) return;
        ready.disconnect();
        form.requestSubmit(button);
      });
      ready.observe(form, { attributes: true, attributeFilter: ['data-hcaptcha-bound'] });
      window.setTimeout(() => ready.disconnect(), 15000);

      // The check starts loading when a field gets focus; a tapped button gets none on an iPhone
      form.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    }

    window.clearTimeout(Number(form.dataset.sendTimer));
    const giveUp = () => {
      if (puzzleOpen()) {
        form.dataset.sendTimer = String(window.setTimeout(giveUp, 2000));
        return;
      }
      button.textContent = label;
      if (problem) {
        button.before(problem);
        problem.textContent = button.dataset.failed ?? '';
      }
    };
    form.dataset.sendTimer = String(window.setTimeout(giveUp, 12000));
  });
}

start([
  setUpWrittenLogos,
  watchFooter,
  revealProductCards,
  turnMenuIconOnScroll,
  flipCardPhotoOnDeliberateHover,
  openDrawersFromLinks,
  explainReviewFormProblems,
  showReviewSending,
]);
